import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
}));

/**
 * Tag Management Dialog Component
 * Dialog for managing and viewing all tags
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {Array} allTagsWithStats - All tags with statistics
 * @param {string} currentUser - Current user name
 * @param {Function} getIconComponent - Get icon component by name
 * @param {Function} handleMakeTagPublic - Handler for making tag public
 */
const TagManagementDialog = ({
  open,
  onClose,
  allTagsWithStats,
  currentUser,
  getIconComponent,
  handleMakeTagPublic,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOfferIcon />
          Tag-Verwaltung
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" paragraph>
          Hier können Sie alle Tags verwalten, gruppieren und deren Verwendung sehen.
        </Typography>

        {/* Tag Statistics */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Alle Tags ({allTagsWithStats.length})
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell>Tag Name</StyledTableCell>
                <StyledTableCell>Kategorie</StyledTableCell>
                <StyledTableCell>Sichtbarkeit</StyledTableCell>
                <StyledTableCell>Erstellt von</StyledTableCell>
                <StyledTableCell align="right">Verwendungen</StyledTableCell>
                <StyledTableCell>Aktionen</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allTagsWithStats.map((tag) => {
                const isOwnTag = tag.createdBy === currentUser;
                const CustomIconComponent = tag.icon ? getIconComponent(tag.icon) : null;

                return (
                  <TableRow key={tag.name} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {CustomIconComponent ? (
                          <CustomIconComponent fontSize="small" color="primary" />
                        ) : tag.isPrivate ? (
                          <LockIcon fontSize="small" color="secondary" />
                        ) : (
                          <PublicIcon fontSize="small" color="primary" />
                        )}
                        {tag.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={tag.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tag.isPrivate ? 'Privat' : 'Öffentlich'}
                        size="small"
                        color={tag.isPrivate ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color={isOwnTag ? 'primary' : 'textSecondary'}>
                        {tag.createdBy}
                        {isOwnTag && ' (Sie)'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={tag.count} size="small" color="info" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {tag.isPrivate && isOwnTag && (
                          <Tooltip title="Als öffentlich veröffentlichen">
                            <IconButton
                              size="small"
                              onClick={() => handleMakeTagPublic(tag.name)}
                            >
                              <PublicIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>🔒 Private Tags:</strong> Nur für Sie sichtbar. Können jederzeit von Ihnen gelöscht oder öffentlich gemacht werden.
          </Typography>
          <Typography variant="body2">
            <strong>🌐 Öffentliche Tags:</strong> Für alle Nutzer sichtbar. Data Stewards können öffentliche Tags direkt entfernen.
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagManagementDialog;
